import * as go from 'gojs';
import { noop } from 'vet-bones/utils';

import { GeometryString } from 'src/containers/ProjectDiagram/constants/geometries';
import { Layer } from 'src/containers/ProjectDiagram/constants/layers';
import {
  DELETED_BACKGROUND_COLOR,
  DELETED_OUTLINE_COLOR,
  DRAFT_BACKGROUND_COLOR,
  DRAFT_BACKGROUND_OUTLINE_WIDTH,
  DRAFT_OUTLINE_COLOR,
  LINK_COLOR,
  LINK_LABEL_BACKGROUND_COLOR,
  LINK_LABEL_FONT_FACE,
  LINK_LABEL_FONT_SIZE,
  LINK_LABEL_FONT_WEIGHT,
  LINK_LABEL_MAX_SIZE,
  LINK_LABEL_PADDING,
  MODEL_CONCEPT_MAPPED_ICON_COLOR,
  MODEL_CONCEPT_MAPPED_ICON_SIZE,
} from 'src/containers/ProjectDiagram/constants/styles';
import {
  graphObjectMouseEnter,
  graphObjectMouseLeave,
} from 'src/containers/ProjectDiagram/handlers/graphObject';
import { ExtendedNode } from 'src/containers/ProjectDiagram/templates/extensions/node';
import {
  dataBinding,
  modelBinding,
  objectBinding,
} from 'src/containers/ProjectDiagram/templates/helpers/binding';
import { isRelationshipModified } from 'src/containers/ProjectDiagram/templates/helpers/draft';
import {
  determineColorFromSelection,
  isRelationshipVisible,
} from 'src/containers/ProjectDiagram/templates/helpers/styles';
import { DeletedModelConceptRelationshipLabel } from 'src/containers/ProjectDiagram/types/data';
import { ModelConceptRelationshipLabelNode } from 'src/containers/ProjectDiagram/types/diagram';
import { Model, ModelData } from 'src/containers/ProjectDiagram/types/model';
import { getDiagramPartLabel } from 'src/containers/ProjectDiagram/utils/labels';
import {
  hasMappingsForRelationship,
  hasValidMappingsForRelationship,
} from 'src/containers/ProjectDiagram/utils/mapping/hasValidMappings';

const GEOMETRY_INVALID_MAPPED = go.Geometry.parse(
  GeometryString.INVALID_MAPPED,
  true
);
const GEOMETRY_VALID_MAPPED = go.Geometry.parse(
  GeometryString.VALID_MAPPED,
  true
);

const $ = go.GraphObject.make;

const shouldShowMappedStyle = (modelData: ModelData, shape: go.Shape) => {
  if (modelData.draftData) {
    return false;
  }
  // the only time node.data is missing is when the link
  // is temporary (dragging dangling link), so node.data
  // missing indicates it shouldn't be the mapped color
  // (and it will console error if we key into node.data.key)
  const data = shape.part?.data;
  const model = shape.diagram?.model;
  return Boolean(
    data && model && hasMappingsForRelationship(data)(model as Model)
  );
};

const shouldShowValidMappedStyle = (modelData: ModelData, shape: go.Shape) => {
  if (modelData.draftData) {
    return false;
  }

  // the only time node.data is missing is when the link
  // is temporary (dragging dangling link), so node.data
  // missing indicates it shouldn't be the mapped color
  // (and it will console error if we key into node.data.key)
  const data = shape.part?.data;
  const model = shape.diagram?.model;

  return Boolean(
    data && model && hasValidMappingsForRelationship(data)(model as Model)
  );
};

export const createModelConceptRelationshipLabelTemplate = ({
  deleted = false,

  contextMenu,
  handleClick = noop,
  handleDoubleClick = noop,
  toolTip,
}: {
  deleted?: boolean;

  contextMenu: go.HTMLInfo;
  handleClick?: NonNullable<ModelConceptRelationshipLabelNode['click']>;
  handleDoubleClick?: NonNullable<
    ModelConceptRelationshipLabelNode['doubleClick']
  >;
  toolTip: go.HTMLInfo;
}) =>
  $(
    ExtendedNode,
    'Spot',
    {
      click: handleClick,
      contextMenu,
      cursor: 'pointer',
      doubleClick: handleDoubleClick,
      layerName: Layer.MODEL_CONCEPT_RELATIONSHIP_LABEL_BACKGROUND_LAYER,
      mouseEnter: graphObjectMouseEnter,
      mouseLeave: graphObjectMouseLeave,
      selectionAdorned: false,
      toolTip,
    },

    objectBinding<go.Node, go.Node>()('layerName', 'isSelected', (isSelected) =>
      isSelected
        ? Layer.MODEL_CONCEPT_RELATIONSHIP_LABEL_FOREGROUND_LAYER
        : Layer.MODEL_CONCEPT_RELATIONSHIP_LABEL_BACKGROUND_LAYER
    ),
    modelBinding<go.Shape, ModelData>()('visible', '', isRelationshipVisible),
    $(
      go.Panel, // label container
      'Auto',
      {
        name: 'LABEL',
      },

      // white background
      $(
        go.Shape,
        'RoundedRectangle',
        {
          name: 'LABEL BACKGROUND',
          fill: LINK_LABEL_BACKGROUND_COLOR,
          stroke: LINK_LABEL_BACKGROUND_COLOR,
          strokeWidth: DRAFT_BACKGROUND_OUTLINE_WIDTH,
        },
        modelBinding<go.Shape, ModelData>()(
          'fill',
          '',
          determineColorFromSelection(
            isRelationshipModified,
            deleted ? DELETED_BACKGROUND_COLOR : LINK_LABEL_BACKGROUND_COLOR,
            deleted ? DELETED_BACKGROUND_COLOR : DRAFT_BACKGROUND_COLOR
          )
        ),
        modelBinding<go.Shape, ModelData>()(
          'stroke',
          '',
          determineColorFromSelection(
            isRelationshipModified,
            deleted ? DELETED_OUTLINE_COLOR : LINK_LABEL_BACKGROUND_COLOR,
            deleted ? DELETED_OUTLINE_COLOR : DRAFT_OUTLINE_COLOR
          )
        )
      ),
      $(
        go.Panel,
        'Horizontal',
        $(
          go.Shape, // icon
          {
            width: MODEL_CONCEPT_MAPPED_ICON_SIZE,
            height: MODEL_CONCEPT_MAPPED_ICON_SIZE,
            strokeWidth: 0,
            alignment: go.Spot.Left,
          },
          modelBinding<go.Shape, ModelData>()(
            'visible',
            '',
            (modelData, shape) => shouldShowMappedStyle(modelData, shape)
          ),
          modelBinding<go.Shape, ModelData>()(
            'geometry',
            '',
            (modelData, shape) =>
              shouldShowValidMappedStyle(modelData, shape)
                ? GEOMETRY_VALID_MAPPED
                : GEOMETRY_INVALID_MAPPED
          ),
          modelBinding<go.Shape, ModelData>()(
            'fill',
            '',
            determineColorFromSelection(
              isRelationshipModified,
              MODEL_CONCEPT_MAPPED_ICON_COLOR
            )
          )
        ),
        $(
          go.TextBlock, // the actual label component
          {
            name: 'LABEL TEXT',
            stroke: LINK_COLOR,
            background: 'transparent',
            font: `${LINK_LABEL_FONT_WEIGHT} ${LINK_LABEL_FONT_SIZE}px ${LINK_LABEL_FONT_FACE}`,
            wrap: go.Wrap.None,
            margin: new go.Margin(
              0,
              LINK_LABEL_PADDING,
              LINK_LABEL_PADDING,
              10
            ),
            overflow: go.TextOverflow.Ellipsis,
          },
          deleted
            ? dataBinding<go.TextBlock, DeletedModelConceptRelationshipLabel>()(
                'text'
              )
            : modelBinding<go.TextBlock, ModelData>()(
                'text',
                'labelsByIri',
                getDiagramPartLabel
              ),
          modelBinding<go.TextBlock, ModelData>()(
            'stroke',
            '',
            determineColorFromSelection(isRelationshipModified, LINK_COLOR)
          ),
          objectBinding<go.TextBlock, go.Node>()('scale', '', (node) =>
            node.isHighlighted || node.isSelected ? 1.2 : 1
          ),
          objectBinding<go.TextBlock, go.Node>()(
            'maxSize',
            '',
            (node) =>
              new go.Size(
                node.isHighlighted || node.isSelected
                  ? NaN
                  : LINK_LABEL_MAX_SIZE,
                NaN
              )
          )
        )
      )
    )
  );
